import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { useBanner, type BannerState } from "@/hooks/use-banner";

interface ReviewBannerState extends BannerState {
  remindLater?: number | null;
  ratedUs?: boolean;
  activatedAt?: number | null;
}

export const ReviewBanner = () => {
  const { isVisible, updateState, handleDismiss } = useBanner({
    slug: 'review-banner',
    defaultState: {
      dismissed: false,
      dismissedAt: null,
      remindLater: null,
      ratedUs: false,
      activatedAt: null,
    },
    visibilityCheck: (state: ReviewBannerState) => {
      // Don't show if user already rated us
      if (state.ratedUs) {
        return false;
      }

      // Don't show if permanently dismissed
      if (state.dismissed) {
        return false;
      }

      // Check if "remind me later" period has passed (7 days)
      if (state.remindLater) {
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();
        if (now - state.remindLater < sevenDaysInMs) {
          return false;
        }
      }

      // Only show after plugin has been activated for at least 5 days
      if (state.activatedAt) {
        const fiveDaysInMs = 5 * 24 * 60 * 60 * 1000; // 5 days
        const now = Date.now();
        if (now - state.activatedAt < fiveDaysInMs) {
          return false;
        }
      } else {
        // If we don't have activatedAt yet, don't show
        return false;
      }

      return true;
    },
  });

  const handleRateNow = async () => {
    await updateState({
      ratedUs: true,
      dismissed: true,
      dismissedAt: Date.now(),
    });

    // Open WordPress.org review page
    window.open(
      "https://wordpress.org/support/plugin/accessibility-plus/reviews/#new-post",
      "_blank"
    );
  };

  const handleRemindLater = async () => {
    await updateState({
      remindLater: Date.now(),
      dismissed: false,
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="cy:relative cy:mb-6 cy:border-gray-200 cy:bg-gray-50 cy:shadow-sm cy:rounded-none">
      <CardContent className=" cy:px-6">
        {/* Dismiss button - top right */}
        <button
          onClick={handleDismiss}
          className="cy:absolute cy:top-2 cy:right-4 cy:text-gray-400 cy:hover:text-gray-600 cy:transition-colors cy:flex cy:items-center cy:gap-1 cy:!text-[13px]"
          aria-label="Dismiss banner"
        >
          <X className="cy:w-4 cy:h-4" /> Dismiss
        </button>

        {/* Content */}
        <div>
          <p className="cy:text-[13px] cy:text-gray-700 cy:mb-4 cy:leading-relaxed cy:pr-20">
            <span className="cy:font-bold cy:text-gray-900 cy:text-[15px]">Enjoying Accessibility Tool Kit?</span>{" "}
            Hi there! We hope the{" "}
            <a
              href="https://wordpress.org/plugins/accessibility-plus/"
              target="_blank"
              rel="noopener noreferrer"
              className="cy:font-bold cy:text-[15px] cy:text-blue-600 cy:hover:underline"
            >
              Accessibility Tool Kit plugin
            </a>{" "}
            has helped make your website more inclusive and user-friendly. Your
            feedback means a lot! It helps us improve and also helps others
            discover the plugin.
          </p>

          {/* Action Buttons */}
          <div className="cy:flex cy:gap-3 cy:pt-2">
            <Button
              onClick={handleRateNow}
              className="cy:bg-[#ff9500] cy:hover:bg-[#e6c200] cy:text-gray-900 cy:font-semibold cy:px-6 cy:py-2 cy:rounded-none cy:transition-colors cy:!text-[13px]"
            >
              Rate us now
            </Button>
            <Button
              onClick={handleRemindLater}
              variant="outline"
              className="cy:border-gray-300 cy:text-gray-700 cy:hover:bg-gray-100 cy:px-6 cy:py-2 cy:rounded-none cy:transition-colors cy:!text-[13px]"
            >
              Remind me later
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

